import { usePreserveCallback } from '@teamsparta/react';
import { noop } from '@teamsparta/utils';
import { useCallback, useEffect, useRef, useState } from 'react';

export type Position = {
  x: number;
  y: number;
};

type UseDragProps = {
  initialPosition?: Position;
  onDragStart?: (position: Position) => void;
  onDragMove?: (position: Position) => void;
  onDragEnd?: (position: Position) => void;
};

/**
 * useDrag 훅은 HTML 요소의 드래그 기능을 구현할 수 있게 해주는 커스텀 훅입니다.
 * @description 요소의 드래그 상태와 위치를 관리하며, 데스크탑(마우스)과 모바일(터치) 환경을 모두 지원합니다.
 *
 * @template T 드래그 대상 HTML 요소의 타입
 * @param {UseDragProps} props - 드래그 설정 객체
 * @param {Position} [props.initialPosition] - 초기 위치 좌표 (기본값: {x: 0, y: 0})
 * @param {(position: Position) => void} [props.onDragStart] - 드래그 시작 시 호출될 콜백 함수
 * @param {(position: Position) => void} [props.onDragMove] - 드래그 이동 시 호출될 콜백 함수
 * @param {(position: Position) => void} [props.onDragEnd] - 드래그 종료 시 호출될 콜백 함수
 *
 * @returns {{
 *   ref: React.RefObject<T>, // 드래그할 요소에 연결할 ref
 *   position: Position, // 현재 요소의 위치
 *   setPosition: React.Dispatch<React.SetStateAction<Position>>, // 위치를 직접 설정하는 함수
 *   isDragging: boolean, // 현재 드래그 중인지 여부
 *   onMouseDown: (e: React.MouseEvent) => void, // 마우스 이벤트 핸들러
 *   onTouchStart: (e: React.TouchEvent) => void // 터치 이벤트 핸들러
 * }}
 *
 * @example
 * const DraggableComponent = () => {
 *   const { ref, position, onMouseDown, onTouchStart } = useDrag<HTMLDivElement>({
 *     initialPosition: { x: 100, y: 100 },
 *     onDragEnd: (position) => console.log('드래그 종료:', position),
 *   });
 *
 *   return (
 *     <div
 *       ref={ref}
 *       style={{
 *         position: 'absolute',
 *         left: `${position.x}px`,
 *         top: `${position.y}px`
 *       }}
 *       onMouseDown={onMouseDown}
 *       onTouchStart={onTouchStart}
 *     >
 *       드래그 가능한 요소
 *     </div>
 *   );
 * };
 */
export function useDrag<T extends HTMLElement>({
  initialPosition = { x: 0, y: 0 },
  onDragStart = noop,
  onDragMove = noop,
  onDragEnd = noop,
}: UseDragProps) {
  const elementRef = useRef<T>(null);
  const [position, setPosition] = useState<Position>(initialPosition);
  const isDragging = useRef(false);
  const dragOffset = useRef<Position>({ x: 0, y: 0 });

  const onDragStartCallback = usePreserveCallback(onDragStart);
  const onDragMoveCallback = usePreserveCallback(onDragMove);
  const onDragEndCallback = usePreserveCallback(onDragEnd);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!elementRef.current) {
        return;
      }

      /**
       * NOTE: 드래그 중에는 스크롤 방지
       */
      e.preventDefault();

      isDragging.current = true;
      const rect = elementRef.current.getBoundingClientRect();
      const touch = e.touches[0];

      dragOffset.current = {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top,
      };

      onDragStartCallback(position);
    },
    [onDragStartCallback, position],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!elementRef.current) {
        return;
      }

      isDragging.current = true;
      const rect = elementRef.current.getBoundingClientRect();

      dragOffset.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };

      onDragStartCallback(position);
      e.preventDefault();
    },
    [onDragStartCallback, position],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging.current) {
        return;
      }

      const newPosition = {
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y,
      };

      setPosition(newPosition);
      onDragMoveCallback(newPosition);
    },
    [onDragMoveCallback],
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging.current) {
        return;
      }

      /**
       * NOTE: 드래그 중에는 스크롤 방지
       */
      e.preventDefault();

      const touch = e.touches[0];
      const newPosition = {
        x: touch.clientX - dragOffset.current.x,
        y: touch.clientY - dragOffset.current.y,
      };

      setPosition(newPosition);
      onDragMoveCallback(newPosition);
    },
    [onDragMoveCallback],
  );

  const handleMouseUp = useCallback(() => {
    if (!isDragging.current) {
      return;
    }

    isDragging.current = false;
    onDragEndCallback(position);
  }, [onDragEndCallback, position]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging.current) {
      return;
    }

    isDragging.current = false;
    onDragEndCallback(position);
  }, [onDragEndCallback, position]);

  useEffect(() => {
    // 마우스 이벤트 (데스크탑)
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // 터치 이벤트 (모바일)
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
    document.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      // 마우스 이벤트 해제
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      // 터치 이벤트 해제
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  return {
    ref: elementRef,
    position,
    setPosition,
    isDragging: isDragging.current,
    onMouseDown: handleMouseDown,
    onTouchStart: handleTouchStart,
  };
}
